import { trackAddToCart, type EcommerceProductData } from '../analytics/ecommerce';
import { getAttribution } from '../helpers/cart-attribution';
import {
  clearGuestCartId,
  getActiveCartId,
  getAuthenticatedCartId,
  getGuestCartId,
  setAuthenticatedCartId,
  setGuestCartId,
  setGuestCartIdTtlMinutes
} from '../helpers/cart-storage';
import { showBanner } from '../shared/banner';
import { GetLocalizedStringRequest, LocalizationApiClient } from './listing/api/client';
import { getCurrentApiLanguage, getEnvironmentUrl } from './listing/listing-environment';
import { HEADER_CONTEXT_READY_EVENT } from './shared-header-context';

const DEFAULT_GRAPHQL_URL = (import.meta.env.VITE_GRAPHQL_URL as string | undefined) ?? '/graphql';
const DEFAULT_STORE_CODE = 'v_en_gl';

let graphqlUrl = DEFAULT_GRAPHQL_URL;
let storeCode = DEFAULT_STORE_CODE;
let cartTotalSumOfItemsSkus = new Set<string>();
let addToCartController: AbortController | null = null;

interface GqlCartItem {
  uid: string;
  quantity: number;
  product: {
    categories?: Array<{ name?: string }>;
    name: string;
    sku: string;
  };
  prices: { row_total: { value: number; currency: string } };
}

interface GqlCart {
  id: string;
  total_quantity: number;
  items?: GqlCartItem[];
}

interface GqlResponse<T> {
  data?: T;
  errors?: Array<{ message: string; path?: Array<string | number> }>;
}

interface AddToCartRequest {
  requestId: string;
  planName: string;
  items?: AddToCartLine[];
  // Backwards-compatible single-line shape used by pricing cards/configurators.
  sku?: string;
  parentSku?: string;
  quantity?: number;
  ecommerceProduct?: EcommerceProductData | null;
}

interface GqlConfigurableVariant {
  product: { sku: string };
  attributes: Array<{ uid: string }> | null;
}

interface CartItemInput {
  sku: string;
  quantity: number;
  selected_options?: string[];
}

interface AddToCartLine extends CartItemInput {
  ecommerceProduct?: EcommerceProductData | null;
}

interface AddToCartResult {
  requestId: string;
  success: boolean;
  cartCount?: number;
  error?: string;
}

interface CartUserError {
  code: string;
  message: string;
}

type LocalizationClient = Pick<LocalizationApiClient, 'findLocalizedString'>;

const ADD_TO_CART_ERROR_LOCALIZATION_PREFIX = 'AddToCart.Errors.';

export function assertNoCartUserErrors(userErrors: CartUserError[]): void {
  if (userErrors.length > 0) {
    throw new Error(userErrors[0]?.message ?? 'Add to cart failed');
  }
}

export async function resolveCartUserErrorMessage(
  userError: CartUserError,
  client: LocalizationClient = new LocalizationApiClient(getEnvironmentUrl())
): Promise<string> {
  const adobeMessage = userError.message || 'Add to cart failed';
  const errorCode = userError.code?.trim();
  if (!errorCode) return adobeMessage;

  const localizationKey = `${ADD_TO_CART_ERROR_LOCALIZATION_PREFIX}${errorCode}`;

  try {
    const localizedMessage = await client.findLocalizedString(
      new GetLocalizedStringRequest({
        localizationKey,
        culture: getCurrentApiLanguage()
      })
    );
    const normalizedMessage = localizedMessage?.trim();

    if (normalizedMessage && normalizedMessage !== localizationKey) {
      return normalizedMessage;
    }
  } catch (error) {
    console.warn('[mini-cart] localization lookup failed:', error);
  }

  return adobeMessage;
}

interface CartStateUpdatedDetail {
  cart: GqlCart | null;
  cartId: string | null;
  // True when cartId is the authenticated cart, so listeners don't treat it as anonymous.
  authenticated: boolean;
}

type CartFetchResult =
  | { status: 'success'; cart: GqlCart }
  | { status: 'not-found' }
  | { status: 'error' };

interface MountedMiniCart {
  getCartId: () => string | null;
  isAuthenticated: boolean;
  applyCart: (cart: GqlCart) => void;
  applyMissingCart: () => void;
}

const mountedMiniCarts = new Set<MountedMiniCart>();
let cartRefreshPromise: Promise<void> | null = null;
let pageShowListenerRegistered = false;

function notifyCartStateUpdated(
  cartId: string | null,
  cart: GqlCart | null = null,
  authenticated = false
): void {
  document.dispatchEvent(
    new CustomEvent<CartStateUpdatedDetail>('bentley:cart-state-updated', {
      detail: { cart, cartId, authenticated }
    })
  );
}

/**
 * `tolerateErrorPaths` names root fields whose failure must not fail the call.
 * Attribution is best-effort: a hard error there comes back as a null field plus
 * an `errors[]` entry, and it must never block add-to-cart.
 */
async function gqlFetch<T>(
  query: string,
  variables: Record<string, unknown> = {},
  { tolerateErrorPaths = [] }: { tolerateErrorPaths?: readonly string[] } = {}
): Promise<T> {
  const res = await fetch(graphqlUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Store: storeCode
    },
    body: JSON.stringify({ query, variables })
  });

  if (!res.ok) {
    // The cart proxy answers a rejected document with { error }. Surfacing it
    // lets callers tell "the proxy refused this shape" from a transport failure.
    const proxyError = await res
      .clone()
      .json()
      .then((body: { error?: string }) => body?.error)
      .catch(() => undefined);

    throw new Error(
      proxyError
        ? `GraphQL request failed: ${res.status} ${proxyError}`
        : `GraphQL request failed: ${res.status}`
    );
  }

  const json: GqlResponse<T> = await res.json();

  const fatalErrors = json.errors?.filter(
    error => !tolerateErrorPaths.includes(String(error.path?.[0] ?? ''))
  );

  if (fatalErrors?.length) {
    throw new Error(fatalErrors[0]?.message ?? 'GraphQL error');
  }

  if (json.errors?.length && json.errors.length !== fatalErrors?.length) {
    console.warn(
      '[mini-cart] tolerated GraphQL errors:',
      json.errors.map(error => error.message)
    );
  }

  if (!json.data) {
    throw new Error('No data returned from GraphQL');
  }

  return json.data;
}

function clearStoredCartId(notify = true): void {
  clearGuestCartId();

  if (notify) {
    notifyCartStateUpdated(null, null);
  }
}

async function createEmptyCart(): Promise<string> {
  const data = await gqlFetch<{ createEmptyCart: string }>(`mutation { createEmptyCart }`);
  return data.createEmptyCart;
}

function isCartNotFoundError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /could not find a cart|cart (?:was )?not found|invalid cart/i.test(error.message)
  );
}

async function fetchCartState(cartId: string): Promise<CartFetchResult> {
  try {
    const data = await gqlFetch<{ cart: GqlCart }>(
      `query($cartId: String!) {
        cart(cart_id: $cartId) {
          id
          total_quantity
          items {
            uid
            quantity
            product { name sku }
            prices { row_total { value currency } }
          }
        }
      }`,
      { cartId }
    );
    return { status: 'success', cart: data.cart };
  } catch (error) {
    return isCartNotFoundError(error) ? { status: 'not-found' } : { status: 'error' };
  }
}

async function fetchCart(cartId: string): Promise<GqlCart | null> {
  const result = await fetchCartState(cartId);
  return result.status === 'success' ? result.cart : null;
}

async function getOrCreateCartId(): Promise<string> {
  // When authenticated, always use the server-owned cart; never create a guest cart.
  const authenticatedCartId = getAuthenticatedCartId();
  if (authenticatedCartId) {
    return authenticatedCartId;
  }

  const stored = getGuestCartId();
  // Trust the stored guest cart id. Validating with a full cart query before every
  // add doubles latency; recover via recreate-on-not-found in addCartItems instead.
  if (stored) {
    return stored;
  }

  const cartId = await createEmptyCart();
  setGuestCartId(cartId);
  return cartId;
}

// Silent SSO authenticates without carrying the anonymous cart id, so merge any leftover
// guest cart here. Runs through the authenticated proxy, which adds the customer token.
let inflightGuestMerge: Promise<void> | null = null;
const mergedGuestCartIds = new Set<string>();

export async function mergeGuestCartIfPresent(destinationCartId: string): Promise<void> {
  if (inflightGuestMerge) {
    return inflightGuestMerge;
  }

  const guestCartId = getGuestCartId();
  if (!guestCartId || guestCartId === destinationCartId) {
    return;
  }

  if (mergedGuestCartIds.has(guestCartId)) {
    return;
  }

  inflightGuestMerge = (async () => {
    try {
      await gqlFetch<{ mergeCarts: { id: string } | null }>(
        `mutation($sourceCartId: String!, $destinationCartId: String!) {
          mergeCarts(source_cart_id: $sourceCartId, destination_cart_id: $destinationCartId) {
            id
          }
        }`,
        { sourceCartId: guestCartId, destinationCartId }
      );
      mergedGuestCartIds.add(guestCartId);
      clearGuestCartId();
    } catch {
      // Merge failed (e.g. not yet whitelisted) — keep the guest cart and retry next load.
    } finally {
      inflightGuestMerge = null;
    }
  })();

  return inflightGuestMerge;
}

// uids are stable per environment, so no invalidation needed within a session
const optionUidCache = new Map<string, string[]>();

// Magento rebuilds every cart line (prices, options) when items are selected on
// addProductsToCart. A 12-item cart makes add feel broken; id + qty is enough
// for the badge, and the panel refreshes the full cart in the background.
const ADD_PRODUCTS_CART_SELECTION = `
  cart {
    id
    total_quantity
    items {
      quantity
      product { sku }
    }
  }
  user_errors { code message }
`;

export function shouldResolveConfigurableOptions(parentSku?: string, childSku?: string): boolean {
  const parent = parentSku?.trim();
  const child = childSku?.trim();
  if (!parent || !child) return false;
  if (parent.toLowerCase() === child.toLowerCase()) return false;

  // CMS ProductCode is often a joined list of child SKUs (e.g. "14394-14380-14379").
  const tokens = parent.split('-').map(token => token.trim().toLowerCase());
  return !tokens.includes(child.toLowerCase());
}

// option uids differ between environments (staging/prod) — never hardcode them
async function resolveConfigurableOptionUids(
  parentSku: string,
  childSku: string
): Promise<string[]> {
  const cacheKey = `${parentSku}::${childSku}`;
  const cached = optionUidCache.get(cacheKey);
  if (cached) return cached;

  const data = await gqlFetch<{
    products: { items: Array<{ variants?: GqlConfigurableVariant[] | null }> };
  }>(
    `query($parentSku: String!) {
      products(filter: { sku: { eq: $parentSku } }) {
        items {
          ... on ConfigurableProduct {
            variants {
              product { sku }
              attributes { uid }
            }
          }
        }
      }
    }`,
    { parentSku }
  );

  // trim + case-insensitive to match how the .NET sync compares CMS vs Adobe SKUs
  const normalizedChildSku = childSku.trim().toLowerCase();
  const variants = data.products.items[0]?.variants ?? [];
  const match = variants.find(v => v.product.sku.trim().toLowerCase() === normalizedChildSku);
  const uids = (match?.attributes ?? []).map(a => a.uid).filter(Boolean);
  if (uids.length) optionUidCache.set(cacheKey, uids);
  return uids;
}

/**
 * True when the cart proxy refused the document itself, rather than the request
 * failing in transit or Commerce rejecting its contents.
 */
export function isOperationNotAllowedError(error: unknown): boolean {
  return error instanceof Error && /operation not allowed/i.test(error.message);
}

/**
 * Add-to-cart, carrying any Channel Partner attribution the session holds.
 *
 * Both root fields go in ONE `mutation { ... }` block: GraphQL executes root
 * mutation fields serially in document order, so attribution lands on the cart
 * before the add runs, in a single HTTP request. Two operations in one body
 * would not do this.
 *
 * The attribution write is an upsert and is idempotent, so there is no need to
 * branch on whether the cart already carries it. Untagged sessions send the
 * plain add, keeping ordinary traffic on exactly the path it uses today.
 */
export async function addCartItems(cartId: string, cartItems: CartItemInput[]): Promise<GqlCart> {
  type AddProductsToCartResponse = {
    addProductsToCart: { cart: GqlCart; user_errors: CartUserError[] };
    setAttributionOnCart?: { user_errors: CartUserError[] } | null;
  };

  const attribution = getAttribution();
  const hasAttribution = attribution.length > 0;

  const query = hasAttribution
    ? `mutation($cartId: String!, $cartItems: [CartItemInput!]!, $attribution: [CartAttributionInput!]!) {
      setAttributionOnCart(input: { cart_id: $cartId, attribution: $attribution }) {
        cart { attribution { key value } }
        user_errors { code message }
      }
      addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
        ${ADD_PRODUCTS_CART_SELECTION}
      }
    }`
    : `mutation($cartId: String!, $cartItems: [CartItemInput!]!) {
      addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
        ${ADD_PRODUCTS_CART_SELECTION}
      }
    }`;

  let data: AddProductsToCartResponse;
  try {
    data = await gqlFetch<AddProductsToCartResponse>(
      query,
      hasAttribution ? { cartId, cartItems, attribution } : { cartId, cartItems },
      { tolerateErrorPaths: ['setAttributionOnCart'] }
    );
  } catch (error) {
    // The cart proxy only forwards setAttributionOnCart once its allowlist has
    // been updated, and frontend and backend ship down separate pipelines. Until
    // both are deployed, a refused document falls back to the plain add: the
    // shopper still buys, we just lose the attribution on that order. The refusal
    // happens before anything executes, so this cannot double-add.
    if (!hasAttribution || !isOperationNotAllowedError(error)) {
      throw error;
    }

    console.warn(
      '[mini-cart] cart proxy refused the attribution batch; adding without attribution'
    );

    data = await gqlFetch<AddProductsToCartResponse>(
      `mutation($cartId: String!, $cartItems: [CartItemInput!]!) {
        addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
          ${ADD_PRODUCTS_CART_SELECTION}
        }
      }`,
      { cartId, cartItems }
    );
  }

  // Attribution failures are reported, never thrown: losing partner attribution
  // must not cost the shopper the add.
  const attributionErrors = data.setAttributionOnCart?.user_errors;
  if (attributionErrors?.length) {
    console.warn(
      '[mini-cart] setAttributionOnCart user_errors:',
      attributionErrors.map(e => e.message)
    );
  }

  const { cart, user_errors } = data.addProductsToCart;
  const firstUserError = user_errors?.[0];
  if (firstUserError) {
    console.warn(
      '[mini-cart] user_errors:',
      user_errors.map(e => e.message)
    );
    throw new Error(await resolveCartUserErrorMessage(firstUserError));
  }
  return cart;
}

async function addCartItemsRecoveringGuestCart(
  cartId: string,
  cartItems: CartItemInput[]
): Promise<{ cartId: string; cart: GqlCart }> {
  try {
    return { cartId, cart: await addCartItems(cartId, cartItems) };
  } catch (error) {
    // Authenticated carts are server-owned; do not replace them with a guest cart.
    if (!isCartNotFoundError(error) || getAuthenticatedCartId()) {
      throw error;
    }

    clearStoredCartId(false);
    const newCartId = await createEmptyCart();
    setGuestCartId(newCartId);
    return { cartId: newCartId, cart: await addCartItems(newCartId, cartItems) };
  }
}

async function updateCartItemQuantity(
  cartId: string,
  cartItemUid: string,
  quantity: number
): Promise<GqlCart> {
  const data = await gqlFetch<{ updateCartItems: { cart: GqlCart } }>(
    `mutation($cartId: String!, $cartItems: [CartItemUpdateInput!]!) {
      updateCartItems(cartId: $cartId, cartItems: $cartItems) {
        cart {
          id
          total_quantity
          items {
            uid
            quantity
            product { name sku }
            prices { row_total { value currency } }
          }
        }
      }
    }`,
    { cartId, cartItems: [{ cart_item_uid: cartItemUid, quantity }] }
  );
  return data.updateCartItems.cart;
}

async function removeCartItem(cartId: string, cartItemUid: string): Promise<GqlCart> {
  const data = await gqlFetch<{ removeItemFromCart: { cart: GqlCart } }>(
    `mutation($cartId: String!, $cartItemUid: ID!) {
      removeItemFromCart(input: { cart_id: $cartId, cart_item_uid: $cartItemUid }) {
        cart {
          id
          total_quantity
          items {
            uid
            quantity
            product { name sku }
            prices { row_total { value currency } }
          }
        }
      }
    }`,
    { cartId, cartItemUid }
  );
  return data.removeItemFromCart.cart;
}

function formatPrice(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  } catch {
    return `$${value.toLocaleString()}`;
  }
}

// product names (Magento/CMS) and urls are interpolated into innerHTML and attributes
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function parseCartTotalSumOfItemsSkus(value?: string): Set<string> {
  if (!value) return new Set();

  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return new Set();

    return new Set(
      parsed
        .filter((sku): sku is string => typeof sku === 'string')
        .map(sku => sku.trim().toLowerCase())
        .filter(Boolean)
    );
  } catch {
    return new Set();
  }
}

export function cartQty(cart: GqlCart, sumOfItemsSkus = cartTotalSumOfItemsSkus): number {
  const items = cart.items ?? [];
  const containsConfiguredSku = items.some(item =>
    sumOfItemsSkus.has(item.product.sku.trim().toLowerCase())
  );

  if (containsConfiguredSku) {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }

  if (typeof cart.total_quantity === 'number') {
    return cart.total_quantity;
  }

  return items.reduce((sum, item) => sum + item.quantity, 0);
}

function updateCartBadge(totalQuantity: number): void {
  const triggers = document.querySelectorAll<HTMLElement>('[data-mini-cart-trigger]');
  if (triggers.length === 0) return;

  const label = totalQuantity > 0 ? `Cart, ${totalQuantity} items` : 'Cart';
  const displayText = totalQuantity > 99 ? '99+' : String(totalQuantity);

  triggers.forEach(trigger => {
    trigger.setAttribute('aria-label', label);

    let badge = trigger.querySelector<HTMLElement>('.header-cart-button__badge');
    let badgeLabel = trigger.querySelector<HTMLElement>('.header-cart-button__badge-label');

    if (totalQuantity > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'header-cart-button__badge';
        badge.setAttribute('aria-hidden', 'true');
        badgeLabel = document.createElement('span');
        badgeLabel.className = 'header-cart-button__badge-label';
        badge.appendChild(badgeLabel);
        trigger.appendChild(badge);
      }

      if (!badgeLabel) {
        badgeLabel = document.createElement('span');
        badgeLabel.className = 'header-cart-button__badge-label';
        badge.appendChild(badgeLabel);
      }

      badgeLabel.textContent = displayText;
      badgeLabel.dataset.digits = String(displayText.length);
    } else if (badge) {
      badge.remove();
    }
  });
}

/** Shared across tabs so Commerce opened in a new window can still silent-SSO. */
export const PENDING_SILENT_SSO_AFTER_COMMERCE_KEY = 'bentley.pendingSilentSsoAfterCommerce';

export function markPendingSilentSsoAfterCommerce(): void {
  const value = String(Date.now());
  try {
    localStorage.setItem(PENDING_SILENT_SSO_AFTER_COMMERCE_KEY, value);
    return;
  } catch {
    // fall through to sessionStorage
  }
  try {
    sessionStorage.setItem(PENDING_SILENT_SSO_AFTER_COMMERCE_KEY, value);
  } catch {
    // Storage unavailable; Commerce referrer still covers same-tab returns.
  }
}

export function resolveUrl(url: string, cartId: string | null): string {
  if (!url || !cartId) return url;
  return url.replace(/\{(?:anonymousCartId|cartId)\}|%7B(?:anonymousCartId|cartId)%7D/gi, cartId);
}

function setCartLinks(
  root: HTMLElement,
  cartUrl: string,
  checkoutUrl: string,
  backUrl: string
): void {
  root.querySelectorAll<HTMLAnchorElement>('[data-mini-cart-cart-link]').forEach(el => {
    el.href = cartUrl;
  });

  root.querySelectorAll<HTMLAnchorElement>('[data-mini-cart-checkout-link]').forEach(el => {
    el.href = checkoutUrl || cartUrl;
  });

  root.querySelectorAll<HTMLElement>('[data-mini-cart-back-link]').forEach(el => {
    if (backUrl) {
      (el as HTMLAnchorElement).href = backUrl;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  });
}

function isCommerceOutboundAnchor(el: Element | null): el is HTMLAnchorElement {
  if (!(el instanceof HTMLAnchorElement)) return false;
  return (
    el.hasAttribute('data-mini-cart-cart-link') ||
    el.hasAttribute('data-mini-cart-checkout-link') ||
    el.classList.contains('add-to-cart-banner__cart-link')
  );
}

let commerceOutboundListenerRegistered = false;

function registerCommerceOutboundListener(): void {
  if (commerceOutboundListenerRegistered) return;
  commerceOutboundListenerRegistered = true;

  const markIfCommerceAnchor = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a');
    if (!isCommerceOutboundAnchor(anchor)) return;
    markPendingSilentSsoAfterCommerce();
  };

  // Primary click (incl. ctrl/cmd+click new tab) and middle-click (auxclick).
  document.addEventListener('click', markIfCommerceAnchor, true);
  document.addEventListener(
    'auxclick',
    event => {
      if (!(event instanceof MouseEvent) || event.button !== 1) return;
      markIfCommerceAnchor(event);
    },
    true
  );
}

function setCountElements(panel: HTMLElement, totalQuantity: number): void {
  const n = totalQuantity;
  const unit = n === 1 ? 'item' : 'items';

  panel.querySelectorAll<HTMLElement>('[data-mini-cart-count-num]').forEach(el => {
    el.textContent = String(n);
  });
  panel.querySelectorAll<HTMLElement>('[data-mini-cart-count-unit]').forEach(el => {
    el.textContent = unit;
  });
}

function renderItemRow(item: GqlCartItem): string {
  const price = formatPrice(item.prices.row_total.value, item.prices.row_total.currency);
  const name = escapeHtml(item.product.name);
  return `
    <li class="mini-cart__item" data-uid="${item.uid}">
      <div class="mini-cart__item-top">
        <p class="mini-cart__item-name">${name}</p>
        <span class="mini-cart__item-price">${price}</span>
      </div>
      <div class="mini-cart__item-bottom">
        <span class="mini-cart__qty-label">Qty:</span>
        <div class="mini-cart__qty-group">
          <button type="button" class="mini-cart__qty-btn" data-mini-cart-decrease data-uid="${item.uid}" aria-label="Decrease quantity of ${name}">
            <svg class="size-4 fill-current" role="presentation"><use xlink:href="#minus"></use></svg>
          </button>
          <output class="mini-cart__qty-value">${item.quantity}</output>
          <button type="button" class="mini-cart__qty-btn" data-mini-cart-increase data-uid="${item.uid}" aria-label="Increase quantity of ${name}">
            <svg class="size-4 fill-current" role="presentation"><use xlink:href="#plus"></use></svg>
          </button>
        </div>
        <button type="button" class="mini-cart__qty-remove" data-mini-cart-remove data-uid="${item.uid}" aria-label="Remove ${name} from cart">
          <svg class="size-5 fill-current" role="presentation"><use xlink:href="#trash-20"></use></svg>
        </button>
      </div>
    </li>
  `;
}

function renderCompleteView(panel: HTMLElement, cart: GqlCart): void {
  const items = cart.items;
  if (!items?.length) return;

  setCountElements(panel, cartQty(cart));

  const itemsList = panel.querySelector<HTMLElement>('[data-mini-cart-items]');
  if (itemsList) {
    itemsList.innerHTML = items.map(renderItemRow).join('');
  }

  // Phase 2: switch to 'complete' to show item list
  panel.dataset.miniCartView = 'basic';
}

async function withRetry<T>(fn: () => Promise<T>, retries = 1): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (retries <= 0) throw err;
    await new Promise(r => setTimeout(r, 800));
    return withRetry(fn, retries - 1);
  }
}

function showPanelError(panel: HTMLElement, message: string): void {
  const existing = panel.querySelector('.mini-cart__error');
  if (existing) existing.remove();

  const el = document.createElement('p');
  el.className = 'mini-cart__error';
  el.setAttribute('role', 'alert');
  el.textContent = message;

  const content = panel.querySelector('.mini-cart__content');
  if (content) content.prepend(el);

  setTimeout(() => el.remove(), 4000);
}

/** Template HTML is trusted; placeholder values are escaped for text/quoted attributes. */
export function formatCartAlertMessage(
  template: string | undefined,
  planName: string,
  quantity: number,
  cartUrl: string
): string {
  if (!template?.trim()) {
    return `
    You added <strong>${quantity} ${escapeHtml(planName)}</strong> license${quantity !== 1 ? 's' : ''} to your
    <a class="add-to-cart-banner__cart-link" href="${escapeHtml(cartUrl)}">Shopping cart</a>
  `;
  }

  const values = {
    quantity: escapeHtml(String(quantity)),
    product: escapeHtml(planName),
    cartUrl: escapeHtml(cartUrl)
  };

  // One pass prevents placeholder-like text in product names/URLs being expanded again.
  return template.replace(
    /\{\{(quantity|product|cartUrl)\}\}/g,
    (_match, key: keyof typeof values) => values[key]
  );
}

function showToast(planName: string, quantity: number, cartUrl: string, template?: string): void {
  const message = formatCartAlertMessage(template, planName, quantity, cartUrl);

  showBanner(message, { className: 'add-to-cart-banner' });
}

const FOCUSABLE_SELECTORS =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function getFocusableElements(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)).filter(el => {
    if (el.closest('[hidden]')) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

function trapFocus(panel: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const focusable = getFocusableElements(panel);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) return;

  const target = e.target as HTMLElement;
  if (e.shiftKey) {
    if (target === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (target === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

function openPanel(panel: HTMLElement, trigger: HTMLElement): void {
  panel.hidden = false;
  trigger.setAttribute('aria-expanded', 'true');

  const firstFocusable = getFocusableElements(panel)[0];
  if (firstFocusable) {
    firstFocusable.focus();
  } else {
    panel.focus();
  }
}

function closePanel(panel: HTMLElement, trigger: HTMLElement): void {
  panel.hidden = true;
  trigger.setAttribute('aria-expanded', 'false');
}

function refreshCartAfterPageRestore(): Promise<void> {
  if (cartRefreshPromise) return cartRefreshPromise;

  const source = Array.from(mountedMiniCarts).find(instance => instance.getCartId());
  const cartId = source?.getCartId();
  if (!source || !cartId) return Promise.resolve();

  cartRefreshPromise = fetchCartState(cartId)
    .then(result => {
      if (result.status === 'error') return;

      if (result.status === 'not-found') {
        // Only clear a guest id when Adobe confirms the cart no longer exists.
        // Authentication failures and transient network errors retain the current state.
        if (source.isAuthenticated) return;

        clearGuestCartId();
        mountedMiniCarts.forEach(instance => instance.applyMissingCart());
        updateCartBadge(0);
        notifyCartStateUpdated(null, null);
        return;
      }

      mountedMiniCarts.forEach(instance => instance.applyCart(result.cart));
      updateCartBadge(cartQty(result.cart));
      notifyCartStateUpdated(cartId, result.cart, source.isAuthenticated);
    })
    .finally(() => {
      cartRefreshPromise = null;
    });

  return cartRefreshPromise;
}

/**
 * Writes attribution onto a cart that already exists, for the shopper who lands
 * on a tagged link with items already in the cart and checks out without adding
 * anything more. The add path carries its own attribution, so this only covers
 * the case where no further add happens.
 *
 * Best-effort by design: failures are swallowed, never surfaced to the shopper.
 */
async function syncAttributionToExistingCart(cartId: string): Promise<void> {
  const attribution = getAttribution();
  if (!attribution.length) return;

  try {
    await gqlFetch(
      `mutation($cartId: String!, $attribution: [CartAttributionInput!]!) {
        setAttributionOnCart(input: { cart_id: $cartId, attribution: $attribution }) {
          cart { attribution { key value } }
          user_errors { code message }
        }
      }`,
      { cartId, attribution },
      { tolerateErrorPaths: ['setAttributionOnCart'] }
    );
  } catch (error) {
    console.warn('[mini-cart] could not sync attribution to existing cart:', error);
  }
}

function registerPageShowListener(): void {
  if (pageShowListenerRegistered) return;

  pageShowListenerRegistered = true;
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      void refreshCartAfterPageRestore();
    }
  });
}

export function mount(root: HTMLElement): void {
  if (root.dataset.miniCartMounted === 'true') {
    return;
  }

  // Public-cache skeleton renders the cart without commerce URLs and waits for
  // header context hydration. That event can fire before this module subscribes
  // (header script is included first), which left the trigger with no click handler.
  if (root.dataset.hydrate === 'mini-cart' && !root.dataset.cartUrl) {
    let started = false;
    const start = (): void => {
      if (started) return;
      started = true;
      document.removeEventListener(HEADER_CONTEXT_READY_EVENT, start);
      mountMiniCart(root);
    };

    document.addEventListener(HEADER_CONTEXT_READY_EVENT, start);

    // Late subscriber: header may already have applied cartUrl before we listened.
    queueMicrotask(() => {
      if (root.dataset.cartUrl) {
        start();
      }
    });

    // Safety net so a missed/failed hydrate never permanently disables the panel.
    window.setTimeout(start, 2500);
    return;
  }

  mountMiniCart(root);
}

function mountMiniCart(root: HTMLElement): void {
  if (root.dataset.miniCartMounted === 'true') {
    return;
  }
  root.dataset.miniCartMounted = 'true';
  registerCommerceOutboundListener();

  graphqlUrl = root.dataset.graphqlUrl ?? DEFAULT_GRAPHQL_URL;
  storeCode = root.dataset.storeCode ?? DEFAULT_STORE_CODE;
  // NaN when the attribute is absent; the helper ignores it and keeps the default.
  setGuestCartIdTtlMinutes(Number(root.dataset.guestCartTtlMinutes));
  const configuredCartTotalSkus = root.dataset.productSkusToUseCartTotalSumOfItems;
  if (configuredCartTotalSkus !== undefined) {
    cartTotalSumOfItemsSkus = parseCartTotalSumOfItemsSkus(configuredCartTotalSkus);
  }

  const panel = root.querySelector<HTMLElement>('[data-mini-cart-panel]');
  const trigger = root.querySelector<HTMLElement>('[data-mini-cart-trigger]');
  const closeBtn = root.querySelector<HTMLElement>('[data-mini-cart-close]');

  if (!panel || !trigger) return;

  const cartUrl = root.dataset.cartUrl ?? '#';
  const checkoutUrl = root.dataset.checkoutUrl ?? '';
  const backUrl = root.dataset.backUrl ?? '';
  const authenticatedCartId = root.dataset.authenticatedCartId?.trim() ?? '';
  const isAuthenticatedCart = authenticatedCartId.length > 0;

  if (isAuthenticatedCart) {
    setAuthenticatedCartId(authenticatedCartId);
    notifyCartStateUpdated(authenticatedCartId, null, true);
    void mergeGuestCartIfPresent(authenticatedCartId);
  }

  const storedCartId = getActiveCartId();
  if (storedCartId) {
    void syncAttributionToExistingCart(storedCartId);
  }

  setCartLinks(
    root,
    resolveUrl(cartUrl, storedCartId),
    resolveUrl(checkoutUrl, storedCartId),
    backUrl
  );

  let currentCartId: string | null = storedCartId;
  let currentCart: GqlCart | null = null;

  const initialCartCount = parseInt(root.dataset.initialCartCount ?? '0', 10);
  if (initialCartCount > 0) {
    updateCartBadge(initialCartCount);
  }

  if (currentCartId) {
    fetchCart(currentCartId).then(cart => {
      if (cart && cartQty(cart) > 0) {
        currentCart = cart;
        updateCartBadge(cartQty(cart));
      } else if (!cart && !isAuthenticatedCart) {
        clearStoredCartId();
        currentCartId = null;
      }
    });
  }

  mountedMiniCarts.add({
    getCartId: () => currentCartId,
    isAuthenticated: isAuthenticatedCart,
    applyCart: cart => {
      currentCartId = cart.id;
      currentCart = cart;

      setCartLinks(
        root,
        resolveUrl(cartUrl, currentCartId),
        resolveUrl(checkoutUrl, currentCartId),
        backUrl
      );

      if (!panel.hidden) {
        if (cartQty(cart) > 0) {
          renderCompleteView(panel, cart);
        } else {
          panel.dataset.miniCartView = 'empty';
        }
      }
    },
    applyMissingCart: () => {
      currentCartId = null;
      currentCart = null;
      panel.dataset.miniCartView = 'empty';
      setCartLinks(root, '#', '#', backUrl);
    }
  });
  registerPageShowListener();

  trigger.addEventListener('click', () => {
    if (!panel.hidden) {
      closePanel(panel, trigger);
      return;
    }

    if (currentCart && cartQty(currentCart) > 0) {
      // Phase 2: call renderCompleteView(panel, currentCart) here instead
      panel.dataset.miniCartView = 'basic';
    } else {
      panel.dataset.miniCartView = 'empty';
    }

    openPanel(panel, trigger);
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      closePanel(panel, trigger);
      trigger.focus();
    });
  }

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (panel.hidden) return;
    if (e.key === 'Escape') {
      closePanel(panel, trigger);
      trigger.focus();
    }
  });

  // capture phase: runs before any child element's bubble-phase listeners (e.g. third-party a11y widgets)
  panel.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      trapFocus(panel, e);
    },
    { capture: true }
  );

  document.addEventListener('click', (e: MouseEvent) => {
    if (panel.hidden) return;
    if (root.contains(e.target as Node)) return;
    closePanel(panel, trigger);
  });

  panel.addEventListener('click', async (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const btn = target.closest<HTMLElement>(
      '[data-mini-cart-decrease],[data-mini-cart-increase],[data-mini-cart-remove]'
    );
    if (!btn || !currentCart || !currentCartId) return;

    const uid = btn.dataset.uid;
    if (!uid) return;

    const item = currentCart.items?.find(i => i.uid === uid);
    if (!item) return;

    btn.setAttribute('disabled', 'true');
    try {
      await withRetry(async () => {
        if (btn.hasAttribute('data-mini-cart-decrease')) {
          currentCart =
            item.quantity <= 1
              ? await removeCartItem(currentCartId!, uid)
              : await updateCartItemQuantity(currentCartId!, uid, item.quantity - 1);
        } else if (btn.hasAttribute('data-mini-cart-increase')) {
          currentCart = await updateCartItemQuantity(currentCartId!, uid, item.quantity + 1);
        } else if (btn.hasAttribute('data-mini-cart-remove')) {
          currentCart = await removeCartItem(currentCartId!, uid);
        }
      });

      if (cartQty(currentCart) === 0) {
        panel.dataset.miniCartView = 'empty';
      } else {
        renderCompleteView(panel, currentCart);
      }

      updateCartBadge(cartQty(currentCart));
    } catch {
      showPanelError(panel, 'Something went wrong. Please try again.');
    } finally {
      btn.removeAttribute('disabled');
    }
  });

  document.addEventListener('bentley:cart-state-updated', (e: Event) => {
    const { cart, cartId } = (e as CustomEvent<CartStateUpdatedDetail>).detail;
    currentCart = cart;
    currentCartId = cartId;
  });

  if (addToCartController) {
    addToCartController.abort();
  }
  addToCartController = new AbortController();

  document.addEventListener(
    'bentley:add-to-cart-request',
    async (e: Event) => {
      const evt = e as CustomEvent<AddToCartRequest>;
      const { requestId, items, sku, parentSku, quantity, planName, ecommerceProduct } = evt.detail;

      try {
        const normalizedSku = sku?.trim();
        const normalizedQuantity = Number(quantity);
        const hasMultiItemPayload = Array.isArray(items) && items.length > 0;

        if (!hasMultiItemPayload && (!normalizedSku || normalizedQuantity <= 0)) {
          throw new Error('Add to cart request did not include a valid item');
        }

        const normalizedLines: AddToCartLine[] = hasMultiItemPayload
          ? items
              .map(item => ({
                ...item,
                sku: item.sku.trim(),
                quantity: Number(item.quantity),
                selected_options: item.selected_options?.filter(Boolean)
              }))
              .filter(item => item.sku && Number.isFinite(item.quantity) && item.quantity > 0)
          : [];

        if (hasMultiItemPayload && normalizedLines.length !== items.length) {
          throw new Error('Add to cart request included an invalid item');
        }

        // Configurable UID lookup does not depend on cartId, so it can overlap
        // the cart round trip. Skip it when CMS ProductCode is a joined child-SKU
        // list — Magento has no such configurable parent.
        const [resolvedCartId, selectedOptions] = await Promise.all([
          getOrCreateCartId(),
          !hasMultiItemPayload && shouldResolveConfigurableOptions(parentSku, normalizedSku)
            ? resolveConfigurableOptionUids(parentSku!, normalizedSku!)
            : Promise.resolve<string[]>([])
        ]);

        const useConfigurableParent = Boolean(parentSku && selectedOptions.length);

        const cartItems: CartItemInput[] = hasMultiItemPayload
          ? normalizedLines.map(item => ({
              sku: item.sku,
              quantity: item.quantity,
              ...(item.selected_options?.length && { selected_options: item.selected_options })
            }))
          : useConfigurableParent
            ? [
                {
                  sku: parentSku!,
                  quantity: normalizedQuantity,
                  selected_options: selectedOptions
                }
              ]
            : [{ sku: normalizedSku!, quantity: normalizedQuantity }];

        currentCartId = resolvedCartId;
        const addResult = await addCartItemsRecoveringGuestCart(currentCartId, cartItems);
        currentCartId = addResult.cartId;
        currentCart = addResult.cart;
        const refreshedCartId = currentCartId;
        void fetchCart(refreshedCartId).then(cart => {
          if (!cart || currentCartId !== refreshedCartId) return;
          currentCart = cart;
          updateCartBadge(cartQty(cart));
          notifyCartStateUpdated(currentCartId, cart);
        });

        const analyticsLines = hasMultiItemPayload
          ? normalizedLines
          : [
              {
                sku: normalizedSku!,
                quantity: normalizedQuantity,
                ecommerceProduct
              }
            ];

        analyticsLines.forEach(line => {
          if (!line.ecommerceProduct) return;
          trackAddToCart(line.ecommerceProduct, line.quantity);
        });

        document.dispatchEvent(
          new CustomEvent<AddToCartResult>('bentley:add-to-cart-result', {
            detail: { requestId, success: true, cartCount: cartQty(currentCart) }
          })
        );

        updateCartBadge(cartQty(currentCart));
        notifyCartStateUpdated(currentCartId, currentCart);
        document.querySelectorAll<HTMLElement>('[data-module="mini-cart"]').forEach(instance => {
          const instanceCartUrl = instance.dataset.cartUrl ?? '#';
          const instanceCheckoutUrl = instance.dataset.checkoutUrl ?? '';
          const instanceBackUrl = instance.dataset.backUrl ?? '';
          setCartLinks(
            instance,
            resolveUrl(instanceCartUrl, currentCartId),
            resolveUrl(instanceCheckoutUrl, currentCartId),
            instanceBackUrl
          );
        });
        const addedQuantity = cartItems.reduce((total, item) => total + item.quantity, 0);
        showToast(
          planName,
          addedQuantity,
          resolveUrl(cartUrl, currentCartId),
          root.dataset.addedToCartAlertLabel
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';

        document.dispatchEvent(
          new CustomEvent<AddToCartResult>('bentley:add-to-cart-result', {
            detail: { requestId, success: false, error: message }
          })
        );
      }
    },
    { signal: addToCartController.signal }
  );
}
